import {Action, Attributes, AttributesRow, Card, DataStatus, Separator} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetManufacturerQuery, useGetTreeFolderPathQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {ArticlePricingDetails} from '@dms/warehouse/shared';
import {ArticleDetailCardPrices} from '@dms/warehouse/shared-components';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ArticleCardDetails} from '../types/ArticleCardDetails';

interface ArticleDetailCardProps extends RequiredTestIdProps {
  articleDetail: ArticleCardDetails | Nullish;
  prices: ArticlePricingDetails | null;
  currency: string | Nullish;
  isRefreshingArticle: boolean;
  onRefresh: VoidFunction;
}

export function ArticleDetailCard(props: ArticleDetailCardProps) {
  const warehouseId = props.articleDetail?.warehouseId;

  const articleId = props.articleDetail?.articleId;

  const name = props.articleDetail?.name;

  const manufacturerNumber = props.articleDetail?.manufacturerNumber;

  const manufacturerId = props.articleDetail?.manufacturerId;

  const storageLocation = props.articleDetail?.storageLocation;

  const leafId = props.articleDetail?.leafId;

  const {data: manufacturer, isLoading: isManufacturerLoading} = useGetManufacturerQuery(
    {manufacturerId: manufacturerId as string},
    {skip: isNilOrEmpty(manufacturerId)}
  );

  const {data: category, isLoading: isCategoryLoading} = useGetTreeFolderPathQuery(
    {leafId: leafId as string},
    {skip: isNilOrEmpty(leafId)}
  );

  const isLoading = props.isRefreshingArticle || isManufacturerLoading || isCategoryLoading;

  const isItemExisting = isNotNil(articleId);

  const handleEditArticle = () => {
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {warehouseId, id: articleId},
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );
  };

  const actions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.refresh'),
      leftIcon: 'navigation/refresh',
      onClick: props.onRefresh,
    },
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('entity.warehouse.actions.editArticle'),
      leftIcon: 'action/open_in_new',
      onClick: handleEditArticle,
    },
  ];

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.name'),
      value: name,
    },
    {
      label: i18n.t('entity.warehouse.labels.catalogueNumber'),
      value: manufacturerNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.manufacturer'),
      value: manufacturer?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.storageLocation'),
      value: storageLocation,
    },
    {
      label: i18n.t('entity.warehouse.labels.category'),
      value: category?.treeFolder?.pathName,
    },
  ];
  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.warehouse.labels.articleDetail')}
      actions={isItemExisting ? actions : undefined}
      isExpandable
      data-testid={props['data-testid']}
    >
      <VStack>
        <DataStatus isLoading={isLoading} minHeight={70}>
          <Attributes rows={rows} data-testid={suffixTestId('details', props)} />
          <Separator spacing={3} />
          <ArticleDetailCardPrices prices={props.prices} currency={props.currency} />
        </DataStatus>
      </VStack>
    </Card>
  );
}
